import { Box, Stack, TextField, MenuItem, Typography, InputAdornment } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import type { User } from '@esg/esg-global-types';

interface EditUserProfileTabProps {
  draft: User;
  onChange: (patch: Partial<User>) => void;
  /** Validation message for the email field, or `null` when it is acceptable. */
  emailError: string | null;
  /** True while the availability lookup for the entered address is in flight. */
  emailChecking: boolean;
  /** Runs the format + availability check; fired on blur. */
  onEmailBlur: () => void;
  /** Only super admins may change a user's sign-in address. */
  canEditEmail: boolean;
}

const EditUserProfileTab = ({
  draft,
  onChange,
  emailError,
  emailChecking,
  onEmailBlur,
  canEditEmail
}: EditUserProfileTabProps) => (
  <Stack spacing={2} sx={{ p: 3 }}>
    <Typography variant="subtitle2" color="text.secondary">
      Personal details
    </Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
      <TextField
        label="First name"
        value={draft.first_name}
        onChange={(e) => onChange({ first_name: e.target.value })}
      />
      <TextField
        label="Last name"
        value={draft.last_name}
        onChange={(e) => onChange({ last_name: e.target.value })}
      />
    </Box>
    <TextField
      label="Email"
      value={draft.email}
      onChange={(e) => onChange({ email: e.target.value })}
      onBlur={onEmailBlur}
      disabled={!canEditEmail}
      error={!!emailError}
      helperText={
        emailError ??
        (canEditEmail
          ? 'Changing this changes the address the user signs in with.'
          : 'Only a super admin can change the sign-in email.')
      }
      InputProps={{
        endAdornment: emailChecking ? (
          <InputAdornment position="end">
            <CircularProgress size={16} />
          </InputAdornment>
        ) : null
      }}
      required
    />
    <TextField
      label="Phone"
      value={draft.phone}
      onChange={(e) => onChange({ phone: e.target.value })}
    />
    <TextField
      select
      label="User type"
      value={draft.type}
      onChange={(e) => onChange({ type: e.target.value as User['type'] })}
    >
      <MenuItem value="internal">Internal</MenuItem>
      <MenuItem value="external">External</MenuItem>
      <MenuItem value="any">Combination</MenuItem>
    </TextField>
  </Stack>
);

export default EditUserProfileTab;
